import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, PencilSimple, ShieldCheck, SignOut, EnvelopeSimple, LockKey } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api-client';
import type { User } from '@spine-workspace/shared';

function AuthMethodBadge({ method }: { method?: string }) {
  if (method === 'google') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-input border border-border text-text-primary font-sans text-xs font-medium">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Google
      </span>
    );
  }
  if (method === 'github') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-input border border-border text-text-primary font-sans text-xs font-medium">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
        </svg>
        GitHub
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-input border border-border text-text-primary font-sans text-xs font-medium">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
      </svg>
      Email &amp; Password
    </span>
  );
}

function initials(name: string, email: string) {
  const src = name || email;
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

interface ProfileDialogProps {
  onClose: () => void;
}

export default function ProfileDialog({ onClose }: ProfileDialogProps) {
  const { user, logout, setAuth } = useAuthStore();
  const navigate = useNavigate();

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [changePwLoading, setChangePwLoading] = useState(false);
  const [changePwSent, setChangePwSent] = useState(false);
  const [changePwError, setChangePwError] = useState('');

  async function saveName() {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === user?.name) { setEditingName(false); return; }
    setSaving(true);
    setSaveError('');
    try {
      const { user: updated } = await api.patch<{ user: User }>('/api/auth/me', { name: trimmed });
      const token = useAuthStore.getState().token ?? '';
      setAuth(updated, token);
      setEditingName(false);
    } catch {
      setSaveError('Failed to save name. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePasswordRequest() {
    setChangePwLoading(true);
    setChangePwError('');
    try {
      await api.post('/api/auth/change-password/request', {});
      setChangePwSent(true);
    } catch (err) {
      setChangePwError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setChangePwLoading(false);
    }
  }

  function handleLogout() {
    logout();
    onClose();
    navigate('/login');
  }

  if (!user) return null;

  const label = initials(user.name ?? '', user.email);
  const memberSince = user.createdAt ? formatDate(user.createdAt) : '—';
  const avatarBg = user.avatarUrl ? undefined : 'bg-gradient-to-br from-accent to-violet';

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Dialog */}
      <div className="w-full max-w-md bg-bg-surface border border-border rounded-2xl shadow-elevated overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle shrink-0">
          <span className="font-serif text-base font-medium text-text-primary">Profile</span>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {/* Gradient banner + Avatar */}
          <div className="relative">
            <div className="h-16 bg-gradient-to-r from-accent/30 via-violet/20 to-accent/10" />
            <div className="absolute bottom-0 left-5 translate-y-1/2">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-sans font-semibold ring-4 ring-bg-surface overflow-hidden ${avatarBg ?? ''}`}>
                {user.avatarUrl
                  ? <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                  : label
                }
              </div>
            </div>
          </div>

          <div className="px-5 pt-12 pb-5 space-y-5">
            {/* Name */}
            <div className="space-y-1">
              <label className="block font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest">
                Display Name
              </label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveName();
                      if (e.key === 'Escape') setEditingName(false);
                      e.stopPropagation();
                    }}
                    className="flex-1 bg-bg-input border border-accent text-text-primary rounded-md px-3 py-[7px] text-sm font-sans focus:outline-none"
                  />
                  <button
                    onClick={saveName}
                    disabled={saving}
                    className="w-8 h-8 flex items-center justify-center rounded-md bg-accent text-white hover:bg-accent-hover transition-all duration-fast disabled:opacity-50"
                  >
                    <Check size={14} weight="bold" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-md border border-border text-text-secondary hover:bg-bg-hover transition-all duration-fast"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <span className="font-sans text-base font-medium text-text-primary">{user.name || '—'}</span>
                  <button
                    onClick={() => { setNameDraft(user.name ?? ''); setEditingName(true); }}
                    className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-all duration-fast"
                    title="Edit name"
                  >
                    <PencilSimple size={12} />
                  </button>
                </div>
              )}
              {saveError && <p className="text-[11px] text-error font-sans">{saveError}</p>}
            </div>

            {/* Account details */}
            <div className="bg-bg-deepest border border-border-subtle rounded-xl divide-y divide-border-subtle overflow-hidden">
              <div className="px-4 py-3 flex items-start gap-3">
                <EnvelopeSimple size={14} className="text-text-tertiary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-0.5">Email</p>
                  <p className="font-sans text-sm text-text-primary truncate">{user.email}</p>
                </div>
                {user.emailVerified && (
                  <div className="flex items-center gap-1 text-success shrink-0">
                    <ShieldCheck size={12} weight="fill" />
                    <span className="font-sans text-[11px] font-medium">Verified</span>
                  </div>
                )}
              </div>

              <div className="px-4 py-3 flex items-start gap-3">
                <LockKey size={14} className="text-text-tertiary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-1.5">Sign-in method</p>
                  <AuthMethodBadge method={user.authMethod} />
                </div>
              </div>

              <div className="px-4 py-3 flex items-start gap-3">
                <div className="w-3.5 h-3.5 mt-0.5 shrink-0 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-text-tertiary" />
                </div>
                <div>
                  <p className="font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest mb-0.5">Member since</p>
                  <p className="font-sans text-sm text-text-primary">{memberSince}</p>
                </div>
              </div>
            </div>

            {/* Change password (email users only) */}
            {(!user.authMethod || user.authMethod === 'email') && (
              <div className="bg-bg-deepest border border-border-subtle rounded-xl px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-sans text-sm font-medium text-text-primary">Change password</p>
                    <p className="font-sans text-[11px] text-text-tertiary mt-0.5">
                      {changePwSent ? 'Check your inbox — link expires in 1 hour.' : "We'll send a reset link to your email."}
                    </p>
                    {changePwError && <p className="font-sans text-[11px] text-error mt-1">{changePwError}</p>}
                  </div>
                  {changePwSent ? (
                    <div className="flex items-center gap-1.5 text-success shrink-0">
                      <Check size={13} weight="bold" />
                      <span className="font-sans text-xs font-medium">Sent</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleChangePasswordRequest}
                      disabled={changePwLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-text-primary hover:bg-bg-hover font-sans text-xs font-medium transition-all duration-fast shrink-0 disabled:opacity-50"
                    >
                      {changePwLoading ? 'Sending…' : 'Reset'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Sign out */}
            <div className="flex items-center justify-between px-4 py-3 bg-bg-deepest border border-error/20 rounded-xl">
              <div>
                <p className="font-sans text-sm font-medium text-text-primary">Sign out</p>
                <p className="font-sans text-[11px] text-text-tertiary mt-0.5">You'll need to sign in again.</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-error/40 text-error hover:bg-error/10 font-sans text-xs font-medium transition-all duration-fast shrink-0"
              >
                <SignOut size={13} />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
